import {FC, useState, useEffect} from 'react'
import {PageTitle} from '../../../../../_metronic/layout/core'
import {Row, Col, Form, Button, Card, Badge, Spinner, Table} from 'react-bootstrap'
import {toast} from 'react-toastify'
import monitoringSP, {
  type MonitoringSPData,
  type MonitoringSPParams,
} from '../../../../../services/pana/monitoringSP'
import Pagination from 'react-bootstrap/Pagination'
import usePagination from '../../../../../utils/hooks/usePagination'
import {KTSVG} from '../../../../../_metronic/helpers'

const MonitoringSPPage: FC = () => {
  // State
  const [loading, setLoading] = useState(false)
  const [loadingDownload, setLoadingDownload] = useState(false)
  const [data, setData] = useState<MonitoringSPData[]>([])
  const [totalData, setTotalData] = useState(0)
  const [statistics, setStatistics] = useState({
    total: 0,
    sudahUpload: 0,
    belumUpload: 0,
    persentase: 0,
  })
  const [batches, setBatches] = useState<any[]>([])
  const [locusOptions, setLocusOptions] = useState<any[]>([])
  const [searchInput, setSearchInput] = useState('') // For debounced search

  // Pagination & Filter
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
  })

  const [filters, setFilters] = useState<MonitoringSPParams>({
    has_file_bermaterai: undefined,
    id_batch: undefined,
    locus: '',
    q: '',
    sort_field: 'created_at',
    sort_order: 'desc',
  })

  const paginator: any = usePagination({
    totalCount: totalData,
    pageSize: pagination.per_page || 10,
    currentPage: pagination.page || 1,
    siblingCount: 1,
  })

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.per_page, filters])

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.q) {
        setFilters((prev) => ({...prev, q: searchInput}))
        setPagination((prev) => ({...prev, page: 1}))
      }
    }, 500) // 500ms delay

    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput])

  // Helper function to get file URL
  const getFileUrl = (item: MonitoringSPData): string | null => {
    // Priority 1: Check if url field exists directly (full URL from backend)
    if (item.url && typeof item.url === 'string') {
      return item.url
    }
    
    // Priority 2: Check if file_bermaterai is an object with url property
    if (item.file_bermaterai && typeof item.file_bermaterai === 'object') {
      if ('url' in item.file_bermaterai && item.file_bermaterai.url) {
        return item.file_bermaterai.url
      }
      // Try path property if url doesn't exist
      if ('path' in item.file_bermaterai && item.file_bermaterai.path) {
        const basePath = process.env.REACT_APP_BASE_API_URL?.replace('/api/v1', '') || ''
        return `${basePath}/storage/${item.file_bermaterai.path}`
      }
    }
    
    // Priority 3: If file_bermaterai is a string, construct full URL
    if (typeof item.file_bermaterai === 'string' && item.file_bermaterai) {
      const basePath = process.env.REACT_APP_BASE_API_URL?.replace('/api/v1', '') || ''
      // If already a full URL, return as is
      if (item.file_bermaterai.startsWith('http://') || item.file_bermaterai.startsWith('https://')) {
        return item.file_bermaterai
      }
      // Otherwise construct full URL
      return `${basePath}/storage/${item.file_bermaterai}`
    }
    
    return null
  }

  // Helper function to check if file exists
  const hasFile = (item: MonitoringSPData): boolean => {
    return !!getFileUrl(item)
  }

  const fetchData = async () => {
    setLoading(true)
    try {
      const params: MonitoringSPParams = {
        page: pagination.page,
        per_page: pagination.per_page,
        ...filters,
      }

      const response: any = await monitoringSP.getList(params)

      // Handle API response structure: {data: {dashboard, filters, data: {data: [...]}}}
      let dataList: MonitoringSPData[] = []
      let total = 0
      let dashboardData = null
      let filtersData = null

      if (response?.data) {
        // Extract dashboard statistics
        dashboardData = response.data.dashboard

        // Extract filters for dropdowns
        filtersData = response.data.filters

        // Extract data list (nested: data.data.data)
        if (response.data.data?.data && Array.isArray(response.data.data.data)) {
          dataList = response.data.data.data
          total = response.data.data.total || 0
        } else if (response.data.data && Array.isArray(response.data.data)) {
          // Fallback: if data.data is directly array
          dataList = response.data.data
          total = response.data.data.length
        }
      }

      console.log('API Response structure:', {
        hasDashboard: !!dashboardData,
        hasFilters: !!filtersData,
        dataListLength: dataList.length,
        totalData: total,
        sampleItem: dataList[0]
      })

      setData(dataList)
      setTotalData(total)

      // Use dashboard data for statistics if available
      if (dashboardData) {
        setStatistics({
          total: dashboardData.total_peserta_lulus || 0,
          sudahUpload: dashboardData.sudah_upload_file || 0,
          belumUpload: dashboardData.belum_upload_file || 0,
          persentase: Math.round(dashboardData.completion_ratio || 0),
        })
      } else {
        // Fallback: Calculate statistics from data
        const sudahUpload = dataList.filter((item: any) => {
          const fileUrl = getFileUrl(item)
          return !!fileUrl
        }).length
        const belumUpload = dataList.length - sudahUpload
        const persentase = dataList.length > 0 ? (sudahUpload / dataList.length) * 100 : 0

        setStatistics({
          total: total,
          sudahUpload,
          belumUpload,
          persentase: Math.round(persentase),
        })
      }

      // Update batches and locus options from filters if available
      if (filtersData?.batch && Array.isArray(filtersData.batch)) {
        setBatches(filtersData.batch)
      }
      if (filtersData?.locus && Array.isArray(filtersData.locus)) {
        setLocusOptions(filtersData.locus)
      }
    } catch (error: any) {
      console.error('Error fetching data:', error)
      toast.error(error?.message || 'Gagal mengambil data')
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (key: string, value: any) => {
    setFilters((prev) => ({...prev, [key]: value}))
    setPagination((prev) => ({...prev, page: 1})) // Reset to page 1
  }

  const handleDownloadSingle = async (item: MonitoringSPData) => {
    try {
      setLoadingDownload(true)
      const fileUrl = getFileUrl(item)
      
      // Option 1: Open file directly if URL is available
      if (fileUrl) {
        window.open(fileUrl, '_blank')
        toast.success('File dibuka di tab baru')
      } 
      // Option 2: Use API download endpoint
      else if (item.file_surat_id) {
        const response = await monitoringSP.downloadFile(item.file_surat_id)
        
        // Handle blob response
        const blob = response instanceof Blob ? response : new Blob([response], {type: 'application/pdf'})
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        
        // Generate filename
        const filename = `SP_Bermaterai_${item.nomor_pendaftaran?.replace(/\//g, '_') || item.users_username || item.file_surat_id}.pdf`
        link.download = filename
        
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        
        toast.success('File berhasil diunduh')
      } else {
        toast.error('File tidak tersedia')
      }
    } catch (error: any) {
      console.error('Error downloading file:', error)
      toast.error(error?.response?.data?.message || 'Gagal mengunduh file')
    } finally {
      setLoadingDownload(false)
    }
  }

  const handleDownloadAll = async () => {
    try {
      setLoadingDownload(true)

      const downloadParams = {
        id_batch: filters.id_batch,
        locus: filters.locus,
        has_file_bermaterai: filters.has_file_bermaterai,
      }

      const response = await monitoringSP.downloadAll(downloadParams)

      // Response is already a blob from API
      const blob = response instanceof Blob ? response : new Blob([response], {type: 'application/zip'})
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().replace(/[-:]/g, '').split('.')[0]
      link.download = `SP_Bermaterai_All_${timestamp}.zip`

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast.success('File ZIP berhasil diunduh')
    } catch (error: any) {
      console.error('Error downloading all files:', error)
      toast.error(error?.message || 'Gagal mengunduh file ZIP')
    } finally {
      setLoadingDownload(false)
    }
  }

  const handleSort = (field: string) => {
    const newOrder = filters.sort_field === field && filters.sort_order === 'asc' ? 'desc' : 'asc'
    setFilters((prev) => ({
      ...prev,
      sort_field: field,
      sort_order: newOrder,
    }))
  }

  const getSortIcon = (field: string) => {
    if (filters.sort_field !== field) {
      return <i className='bi bi-arrow-down-up text-muted' style={{fontSize: '0.8rem'}}></i>
    }
    return filters.sort_order === 'asc' ? (
      <i className='bi bi-arrow-up text-primary' style={{fontSize: '0.8rem'}}></i>
    ) : (
      <i className='bi bi-arrow-down text-primary' style={{fontSize: '0.8rem'}}></i>
    )
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>Monitoring Surat Pernyataan Bermaterai</PageTitle>

      <div className='row g-5 g-xl-8'>
        {/* Statistics Cards */}
        <div className='col-xl-3'>
          <Card className='card-xl-stretch mb-xl-8'>
            <Card.Body className='d-flex align-items-center'>
              <span className='svg-icon svg-icon-primary svg-icon-3x ms-n1'>
                <KTSVG path='/media/icons/duotune/general/gen032.svg' />
              </span>
              <div className='ms-3'>
                <div className='text-gray-900 fw-bolder fs-2'>{statistics.total}</div>
                <div className='text-gray-400 fw-bold fs-7'>Total Peserta Lolos</div>
              </div>
            </Card.Body>
          </Card>
        </div>

        <div className='col-xl-3'>
          <Card className='card-xl-stretch mb-xl-8 bg-light-success'>
            <Card.Body className='d-flex align-items-center'>
              <span className='svg-icon svg-icon-success svg-icon-3x ms-n1'>
                <KTSVG path='/media/icons/duotune/files/fil003.svg' />
              </span>
              <div className='ms-3'>
                <div className='text-success fw-bolder fs-2'>{statistics.sudahUpload}</div>
                <div className='text-gray-700 fw-bold fs-7'>Sudah Upload File</div>
              </div>
            </Card.Body>
          </Card>
        </div>

        <div className='col-xl-3'>
          <Card className='card-xl-stretch mb-xl-8 bg-light-warning'>
            <Card.Body className='d-flex align-items-center'>
              <span className='svg-icon svg-icon-warning svg-icon-3x ms-n1'>
                <KTSVG path='/media/icons/duotune/general/gen044.svg' />
              </span>
              <div className='ms-3'>
                <div className='text-warning fw-bolder fs-2'>{statistics.belumUpload}</div>
                <div className='text-gray-700 fw-bold fs-7'>Belum Upload File</div>
              </div>
            </Card.Body>
          </Card>
        </div>

        <div className='col-xl-3'>
          <Card className='card-xl-stretch mb-xl-8 bg-light-primary'>
            <Card.Body className='d-flex align-items-center'>
              <span className='svg-icon svg-icon-primary svg-icon-3x ms-n1'>
                <KTSVG path='/media/icons/duotune/graphs/gra007.svg' />
              </span>
              <div className='ms-3'>
                <div className='text-primary fw-bolder fs-2'>{statistics.persentase}%</div>
                <div className='text-gray-700 fw-bold fs-7'>Completion Rate</div>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Filter Section */}
      <Card className='mb-5'>
        <Card.Header>
          <Card.Title>Filter & Pencarian</Card.Title>
        </Card.Header>
        <Card.Body>
          {/* Search Field */}
          <Row className='mb-4'>
            <Col md={12}>
              <Form.Group>
                <Form.Label className='fw-semibold text-gray-700'>
                  <i className='bi bi-search me-2'></i>
                  Pencarian Global
                  {searchInput && searchInput !== filters.q && (
                    <Spinner animation='border' size='sm' className='ms-2' />
                  )}
                </Form.Label>
                <div className='position-relative'>
                  <Form.Control
                    type='text'
                    placeholder='Cari berdasarkan nama, username, email, no telepon, nomor pendaftaran, atau judul proposal...'
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className='form-control-lg'
                  />
                  {searchInput && (
                    <Button
                      variant='link'
                      size='sm'
                      className='position-absolute top-50 end-0 translate-middle-y me-2'
                      onClick={() => setSearchInput('')}
                      style={{textDecoration: 'none'}}
                    >
                      <i className='bi bi-x-circle-fill text-muted'></i>
                    </Button>
                  )}
                </div>
                <Form.Text className='text-muted'>
                  Pencarian akan otomatis dilakukan setelah Anda selesai mengetik (500ms delay)
                  {filters.q && (
                    <Badge bg='info' className='ms-2'>
                      Mencari: "{filters.q}"
                    </Badge>
                  )}
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {/* Filter Dropdowns */}
          <Row className='g-3'>
            <Col md={4}>
              <Form.Group>
                <Form.Label className='fw-semibold text-gray-700'>Status Upload</Form.Label>
                <Form.Select
                  value={
                    filters.has_file_bermaterai === undefined
                      ? ''
                      : filters.has_file_bermaterai
                      ? 'true'
                      : 'false'
                  }
                  onChange={(e) =>
                    handleFilterChange(
                      'has_file_bermaterai',
                      e.target.value === '' ? undefined : e.target.value === 'true'
                    )
                  }
                >
                  <option value=''>Semua</option>
                  <option value='true'>Sudah Upload</option>
                  <option value='false'>Belum Upload</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label className='fw-semibold text-gray-700'>Batch</Form.Label>
                <Form.Select
                  value={filters.id_batch || ''}
                  onChange={(e) =>
                    handleFilterChange('id_batch', e.target.value ? parseInt(e.target.value) : undefined)
                  }
                >
                  <option value=''>Semua Batch</option>
                  {batches.map((batch) => (
                    <option key={batch.id} value={batch.id}>
                      Batch {batch.id}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label className='fw-semibold text-gray-700'>Locus</Form.Label>
                <Form.Select
                  value={filters.locus || ''}
                  onChange={(e) => handleFilterChange('locus', e.target.value)}
                >
                  <option value=''>Semua Locus</option>
                  {locusOptions.map((locus) => (
                    <option key={locus.id} value={locus.locus}>
                      {locus.locus}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row className='mt-4'>
            <Col className='d-flex gap-2 justify-content-end'>
              <Button
                variant='light'
                size='sm'
                className='btn-active-light-primary'
                onClick={() => {
                  setSearchInput('')
                  setFilters({
                    has_file_bermaterai: undefined,
                    id_batch: undefined,
                    locus: '',
                    q: '',
                    sort_field: 'created_at',
                    sort_order: 'desc',
                  })
                  setPagination((prev) => ({...prev, page: 1}))
                }}
              >
                <i className='bi bi-arrow-clockwise me-1'></i>
                Reset Filter
              </Button>
              <Button
                variant='primary'
                size='sm'
                onClick={handleDownloadAll}
                disabled={loadingDownload || statistics.sudahUpload === 0}
              >
                {loadingDownload ? (
                  <>
                    <Spinner
                      as='span'
                      animation='border'
                      size='sm'
                      role='status'
                      aria-hidden='true'
                      className='me-2'
                    />
                    Mengunduh...
                  </>
                ) : (
                  <>
                    <i className='bi bi-download me-2'></i>
                    Download Semua ZIP
                  </>
                )}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Data Table */}
      <Card>
        <Card.Header>
          <Card.Title>Data Surat Pernyataan Bermaterai</Card.Title>
          <div className='card-toolbar'>
            <span className='badge badge-light-primary'>
              Menampilkan {data.length} dari {totalData} data
            </span>
          </div>
        </Card.Header>
        <Card.Body className='p-0'>
          <div className='table-responsive'>
            <Table className='table table-row-bordered table-row-gray-300 align-middle gs-0 gy-3'>
              <thead>
                <tr className='fw-bolder text-muted bg-light'>
                  <th className='min-w-50px ps-4'>No</th>
                  <th
                    className='min-w-150px cursor-pointer'
                    onClick={() => handleSort('nama_lengkap')}
                  >
                    Nama Lengkap {getSortIcon('nama_lengkap')}
                  </th>
                  <th
                    className='min-w-120px cursor-pointer'
                    onClick={() => handleSort('users_username')}
                  >
                    Username {getSortIcon('users_username')}
                  </th>
                  <th className='min-w-150px cursor-pointer' onClick={() => handleSort('email')}>
                    Email {getSortIcon('email')}
                  </th>
                  <th className='min-w-120px'>No. Telepon</th>
                  <th className='min-w-100px cursor-pointer' onClick={() => handleSort('locus')}>
                    Locus {getSortIcon('locus')}
                  </th>
                  <th
                    className='min-w-80px cursor-pointer'
                    onClick={() => handleSort('batch')}
                  >
                    Batch {getSortIcon('batch')}
                  </th>
                  <th className='min-w-150px'>Nomor Pendaftaran</th>
                  <th className='min-w-100px text-center'>Status File</th>
                  <th className='min-w-100px text-center'>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={10} className='text-center py-10'>
                      <Spinner animation='border' variant='primary' />
                      <div className='mt-3'>Memuat data...</div>
                    </td>
                  </tr>
                ) : data.length === 0 ? (
                  <tr>
                    <td colSpan={10} className='text-center py-10'>
                      <div className='text-gray-600'>
                        <i className='bi bi-inbox fs-2x mb-3 d-block'></i>
                        Tidak ada data
                      </div>
                    </td>
                  </tr>
                ) : (
                  data.map((item, index) => (
                    <tr key={item.file_surat_id}>
                      <td className='ps-4'>
                        <span className='text-dark fw-bolder d-block fs-7'>
                          {(pagination.page - 1) * pagination.per_page + index + 1}
                        </span>
                      </td>
                      <td>
                        <span className='text-dark fw-bolder d-block fs-7'>
                          {item.nama_lengkap}
                        </span>
                      </td>
                      <td>
                        <span className='text-muted fw-bold'>{item.users_username}</span>
                      </td>
                      <td>
                        <span className='text-muted fw-bold'>{item.email}</span>
                      </td>
                      <td>
                        <span className='text-muted fw-bold'>{item.no_telepon || '-'}</span>
                      </td>
                      <td>
                        <Badge bg='light' text='dark'>
                          {item.locus}
                        </Badge>
                      </td>
                      <td>
                        <Badge bg='info'>{item.batch}</Badge>
                      </td>
                      <td>
                        <span className='text-muted fw-bold text-nowrap'>
                          {item.nomor_pendaftaran}
                        </span>
                      </td>
                      <td className='text-center'>
                        {hasFile(item) ? (
                          <Badge bg='success'>
                            <i className='bi bi-check-circle me-1'></i>
                            Sudah Upload
                          </Badge>
                        ) : (
                          <Badge bg='warning'>
                            <i className='bi bi-exclamation-circle me-1'></i>
                            Belum Upload
                          </Badge>
                        )}
                      </td>
                      <td className='text-center'>
                        {hasFile(item) ? (
                          <Button
                            variant='light-primary'
                            size='sm'
                            onClick={() => handleDownloadSingle(item)}
                            disabled={loadingDownload}
                          >
                            <i className='bi bi-download'></i>
                          </Button>
                        ) : (
                          <span className='text-muted fs-8'>-</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
        <Card.Footer>
          <Row className='align-items-center'>
            <Col md={6}>
              <div className='d-flex align-items-center'>
                <span className='text-muted me-3'>Rows per page:</span>
                <Form.Select
                  style={{width: '80px'}}
                  value={pagination.per_page}
                  onChange={(e) =>
                    setPagination((prev) => ({...prev, per_page: parseInt(e.target.value), page: 1}))
                  }
                >
                  <option value='10'>10</option>
                  <option value='25'>25</option>
                  <option value='50'>50</option>
                  <option value='100'>100</option>
                </Form.Select>
              </div>
            </Col>
            <Col md={6}>
              <div className='d-flex justify-content-end'>
                {paginator && (
                  <Pagination>
                    {paginator?.map((page: any, index: number) => {
                      if (page === '...') {
                        return <Pagination.Ellipsis key={`ellipsis-${index}`} disabled />
                      }
                      return (
                        <Pagination.Item
                          key={page}
                          active={page === pagination.page}
                          onClick={() => setPagination((prev) => ({...prev, page}))}
                        >
                          {page}
                        </Pagination.Item>
                      )
                    })}
                  </Pagination>
                )}
              </div>
            </Col>
          </Row>
        </Card.Footer>
      </Card>
    </>
  )
}

const MonitoringSP: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Dashboard',
            path: '/dashboard',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '/',
            path: '',
            isSeparator: false,
            isActive: false,
          },
        ]}
      >
        Monitoring SP
      </PageTitle>
      <MonitoringSPPage />
    </>
  )
}

export default MonitoringSP
